import { useMemo } from "react";
import clsx from "clsx";
import { formatCurrency } from "@/lib/money";
import { MONTH_LABELS, formatPercent } from "@/pages/reports/reportShared";
import type { YearlyMonthRevenue } from "@/types/camping";

function barHeight(amount: number, max: number): number {
  if (amount <= 0 || max <= 0) return 0;
  return Math.max(4, (amount / max) * 100);
}

function occupancyTone(rate: number): string {
  if (rate >= 85) return "bg-status-orange";
  if (rate >= 65) return "bg-accent";
  if (rate >= 40) return "bg-status-indigo";
  return "bg-status-green";
}

export function MonthRevenueChart({
  months,
  priorMonths,
  currency,
  currentYearLabel,
  priorYearLabel,
  highlightMonth,
}: {
  months: YearlyMonthRevenue[];
  priorMonths: YearlyMonthRevenue[] | null;
  currency: string;
  currentYearLabel: string;
  priorYearLabel?: string;
  highlightMonth?: number | null;
}) {
  const max = useMemo(() => {
    const values = months.map((month) => Number.parseFloat(month.invoiced_total) || 0);
    const priorValues = priorMonths?.map((month) => Number.parseFloat(month.invoiced_total) || 0) ?? [];
    return Math.max(...values, ...priorValues, 1);
  }, [months, priorMonths]);

  const hasPrior = priorMonths != null && priorMonths.length > 0;

  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-4 text-xs text-label-secondary">
        <span className="inline-flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-accent" aria-hidden />
          {currentYearLabel}
        </span>
        {hasPrior && priorYearLabel ? (
          <span className="inline-flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-label-tertiary/55" aria-hidden />
            {priorYearLabel}
          </span>
        ) : null}
      </div>
      <div
        className="grid grid-cols-6 gap-x-1.5 gap-y-3 sm:grid-cols-12"
        role="img"
        aria-label={`Monatsumsätze ${currentYearLabel}${
          priorYearLabel ? ` im Vergleich zu ${priorYearLabel}` : ""
        }`}
      >
        {months.map((item, index) => {
          const amount = Number.parseFloat(item.invoiced_total) || 0;
          const priorAmount = hasPrior
            ? Number.parseFloat(priorMonths?.[index]?.invoiced_total ?? "0") || 0
            : 0;
          const isHighlight = highlightMonth === item.month;
          const title = hasPrior
            ? `${MONTH_LABELS[item.month - 1]}: ${formatCurrency(amount, currency)}, Vorjahr ${formatCurrency(priorAmount, currency)}`
            : `${MONTH_LABELS[item.month - 1]}: ${formatCurrency(amount, currency)}`;
          return (
            <div key={item.month} className="flex flex-col items-center gap-1.5" title={title}>
              <div className="flex h-40 w-full items-end justify-center gap-1">
                {hasPrior ? (
                  <div
                    className="w-2 rounded-t bg-label-tertiary/50 sm:w-2.5"
                    style={{ height: `${barHeight(priorAmount, max)}%` }}
                  />
                ) : null}
                <div
                  className={clsx("w-2 rounded-t sm:w-2.5", isHighlight ? "bg-accent" : "bg-accent/70")}
                  style={{ height: `${barHeight(amount, max)}%` }}
                />
              </div>
              <span
                className={clsx(
                  "text-[10px] sm:text-xs",
                  isHighlight ? "font-semibold text-accent" : "text-label-secondary",
                )}
              >
                {MONTH_LABELS[item.month - 1]}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function OccupancyMonthChart({ months }: { months: YearlyMonthRevenue[] }) {
  return (
    <div>
      <div className="grid grid-cols-6 gap-x-1.5 gap-y-3 sm:grid-cols-12" role="img" aria-label="Auslastung je Monat">
        {months.map((item) => {
          const raw = item.occupancy_rate_percent;
          const rate = raw != null && raw !== "" ? Number.parseFloat(raw) : null;
          const known = rate != null && !Number.isNaN(rate);
          const height = known ? Math.min(100, Math.max(rate > 0 ? 4 : 0, rate)) : 0;
          return (
            <div
              key={item.month}
              className="flex flex-col items-center gap-1.5"
              title={`${MONTH_LABELS[item.month - 1]}: ${formatPercent(raw)}`}
            >
              <div className="flex h-24 w-full items-end justify-center">
                <div
                  className={clsx("w-2.5 rounded-t sm:w-3", known ? occupancyTone(rate) : "bg-fill-tertiary")}
                  style={{ height: `${height}%` }}
                />
              </div>
              <span className="text-[10px] text-label-secondary sm:text-xs">
                {MONTH_LABELS[item.month - 1]}
              </span>
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-xs text-label-tertiary">
        Skala 0–100 %. Grün unter 40 %, Indigo bis 65 %, Blau bis 85 %, Orange darüber.
      </p>
    </div>
  );
}
